import { DraggableLocation } from "@hello-pangea/dnd";
import { cloneDeep, isEmpty } from "lodash";
import {
  IDraggableAlignmentResult,
  IDraggableAlignmentResultSection,
  IResource,
  ISectionGroup,
  SectionGroup,
} from "store/types";
import { v4 as uuidv4 } from "uuid";

const findSectionIndexes = (uid: string, draggableAlignmentResults?: IDraggableAlignmentResult) => {
  let sectionGroupIndex = -1;
  let sectionIndex = -1;
  draggableAlignmentResults &&
    draggableAlignmentResults.sectionGroups &&
    draggableAlignmentResults.sectionGroups.forEach((sectionGroup: ISectionGroup, i) => {
      if (sectionIndex === -1) {
        sectionIndex = sectionGroup.sections.findIndex(
          (section: IDraggableAlignmentResultSection) => section.uid === uid,
        );
        if (sectionIndex !== -1) {
          sectionGroupIndex = i;
        }
      }
    });
  return { sectionGroupIndex, sectionIndex };
};

const isResourceReadOnly = (
  section: IDraggableAlignmentResultSection,
  resourceList: IResource[],
  resourceIndex: number,
) => {
  if (resourceIndex > -1) {
    const uid = section.resourceIds[resourceIndex];
    if (!isEmpty(uid)) {
      const resource = resourceList.find((resource: IResource) => resource.uid === uid);
      if (resource && resource.readOnly) {
        return true;
      }
    }
  }
  return false;
};

export const canBeDraggedToThisPosition = (
  destination: DraggableLocation,
  draggableContent: IDraggableAlignmentResult,
) => {
  // if exclude is empty, then this is a valid position, we move by
  // running the exclude menu option logic
  if (destination.droppableId === "exclude") {
    return true;
  }
  // The order of the resources must adhere to the rules of the template.  Resources being dragged around
  // must be positioned below any readOnly resources.
  const destinationIndexes = findSectionIndexes(destination.droppableId, draggableContent);
  const destinationSection =
    draggableContent.sectionGroups[destinationIndexes.sectionGroupIndex].sections[destinationIndexes.sectionIndex];

  // Find the resources directly below the drop location (it may not exist)
  const belowResourceIndex = destination.index + 1;

  // Return true if moving this resource to the last position
  if (!destinationSection.resourceIds[belowResourceIndex]) {
    return true;
  }

  // Return false if the resource directly below is readOnly
  if (isResourceReadOnly(destinationSection, draggableContent.resources, belowResourceIndex)) {
    return false;
  }

  return true;
};

export const reorder = (
  source: DraggableLocation,
  destination: DraggableLocation,
  draggableId: string,
  draggableContent: IDraggableAlignmentResult,
) => {
  const sourceIndexes = findSectionIndexes(source.droppableId, draggableContent);
  const sourceSection =
    draggableContent.sectionGroups[sourceIndexes.sectionGroupIndex].sections[sourceIndexes.sectionIndex];

  const newResourceIds = Array.from(sourceSection.resourceIds);
  newResourceIds.splice(source.index, 1);
  newResourceIds.splice(destination.index, 0, draggableId);

  const edits = cloneDeep(draggableContent);
  edits.sectionGroups[sourceIndexes.sectionGroupIndex].sections[sourceIndexes.sectionIndex].resourceIds =
    newResourceIds;
  return edits;
};

export const move = (
  source: DraggableLocation,
  destination: DraggableLocation,
  draggableId: string,
  draggableContent: IDraggableAlignmentResult,
) => {
  // support moving to an empty exclude list
  if (destination.droppableId === "exclude") {
    return exclude(source, draggableId, draggableContent);
  }
  const sourceIndexes = findSectionIndexes(source.droppableId, draggableContent);
  const sourceSection =
    draggableContent.sectionGroups[sourceIndexes.sectionGroupIndex].sections[sourceIndexes.sectionIndex];
  const newSourceResourceIds = Array.from(sourceSection.resourceIds);
  newSourceResourceIds.splice(source.index, 1);

  const destinationIndexes = findSectionIndexes(destination.droppableId, draggableContent);
  const destinationSection =
    draggableContent.sectionGroups[destinationIndexes.sectionGroupIndex].sections[destinationIndexes.sectionIndex];
  const newDestinationResourceIds = Array.from(destinationSection.resourceIds);
  newDestinationResourceIds.splice(destination.index, 0, draggableId);

  const edits = cloneDeep(draggableContent);
  // Remove the source section if no resources left within - BUT not if this is the Sorted section
  if (
    isEmpty(newSourceResourceIds) &&
    edits.sectionGroups[sourceIndexes.sectionGroupIndex].name !== SectionGroup.SORTED
  ) {
    edits.sectionGroups[sourceIndexes.sectionGroupIndex].sections.splice(sourceIndexes.sectionIndex, 1);
  } else {
    edits.sectionGroups[sourceIndexes.sectionGroupIndex].sections[sourceIndexes.sectionIndex].resourceIds =
      newSourceResourceIds;
  }
  edits.sectionGroups[destinationIndexes.sectionGroupIndex].sections[destinationIndexes.sectionIndex].resourceIds =
    newDestinationResourceIds;
  return edits;
};

export const moveAll = (
  source: DraggableLocation,
  destination: DraggableLocation,
  draggableIds: string[],
  draggableContent: IDraggableAlignmentResult,
) => {
  const sourceIndexes = findSectionIndexes(source.droppableId, draggableContent);

  const destinationIndexes = findSectionIndexes(destination.droppableId, draggableContent);
  const destinationSection =
    draggableContent.sectionGroups[destinationIndexes.sectionGroupIndex].sections[destinationIndexes.sectionIndex];
  let newDestinationResourceIds = Array.from(destinationSection.resourceIds);

  newDestinationResourceIds = newDestinationResourceIds.concat(draggableIds);

  const edits = cloneDeep(draggableContent);
  edits.sectionGroups[sourceIndexes.sectionGroupIndex].sections.splice(sourceIndexes.sectionIndex, 1);
  edits.sectionGroups[destinationIndexes.sectionGroupIndex].sections[destinationIndexes.sectionIndex].resourceIds =
    newDestinationResourceIds;

  return edits;
};

export const exclude = (source: any, draggableId: string, draggableContent: IDraggableAlignmentResult) => {
  const sourceIndexes = findSectionIndexes(source.droppableId, draggableContent);
  const sourceSection =
    draggableContent.sectionGroups[sourceIndexes.sectionGroupIndex].sections[sourceIndexes.sectionIndex];
  const newSourceResourceIds = Array.from(sourceSection.resourceIds);
  newSourceResourceIds.splice(source.index, 1);

  const edits = cloneDeep(draggableContent);
  // Remove the source section if no resources left within - BUT not if this is the Sorted section
  if (
    isEmpty(newSourceResourceIds) &&
    edits.sectionGroups[sourceIndexes.sectionGroupIndex].name !== SectionGroup.SORTED
  ) {
    edits.sectionGroups[sourceIndexes.sectionGroupIndex].sections.splice(sourceIndexes.sectionIndex, 1);
  } else {
    edits.sectionGroups[sourceIndexes.sectionGroupIndex].sections[sourceIndexes.sectionIndex].resourceIds =
      newSourceResourceIds;
  }

  // Is there already a section in the Exclude sectionGroup with this uid?  If not, we need to add it.
  const excludedSectionGroupIndex = draggableContent.sectionGroups.findIndex(
    (sectionGroup: ISectionGroup) => sectionGroup.name === SectionGroup.EXCLUDED,
  );
  if (excludedSectionGroupIndex !== -1) {
    const destinationIndex = draggableContent.sectionGroups[excludedSectionGroupIndex].sections.findIndex(
      (section: IDraggableAlignmentResultSection) => section.clonedUid && section.clonedUid === sourceSection.uid,
    );
    if (destinationIndex > -1) {
      const newDestinationResourceIds = Array.from(
        edits.sectionGroups[excludedSectionGroupIndex].sections[destinationIndex].resourceIds,
      );
      newDestinationResourceIds.splice(
        edits.sectionGroups[excludedSectionGroupIndex].sections[destinationIndex].resourceIds.length,
        0,
        draggableId,
      );
      edits.sectionGroups[excludedSectionGroupIndex].sections[destinationIndex].resourceIds = newDestinationResourceIds;
    } else {
      edits.sectionGroups[excludedSectionGroupIndex].sections.push({
        ...sourceSection,
        uid: uuidv4(),
        clonedUid: sourceSection.uid,
        resourceIds: [draggableId],
      });
    }
  }

  return edits;
};

export const excludeAll = (source: any, draggableIds: string[], draggableContent: IDraggableAlignmentResult) => {
  const sourceIndexes = findSectionIndexes(source.droppableId, draggableContent);
  const sourceSection =
    draggableContent.sectionGroups[sourceIndexes.sectionGroupIndex].sections[sourceIndexes.sectionIndex];

  const edits = cloneDeep(draggableContent);
  edits.sectionGroups[sourceIndexes.sectionGroupIndex].sections.splice(sourceIndexes.sectionIndex, 1);

  // Is there already a section in the Exclude sectionGroup with this uid?  If not, we need to add it.
  const excludedSectionGroupIndex = draggableContent.sectionGroups.findIndex(
    (sectionGroup: ISectionGroup) => sectionGroup.name === SectionGroup.EXCLUDED,
  );
  if (excludedSectionGroupIndex !== -1) {
    const destinationIndex = draggableContent.sectionGroups[excludedSectionGroupIndex].sections.findIndex(
      (section: IDraggableAlignmentResultSection) => section.clonedUid && section.clonedUid === sourceSection.uid,
    );
    if (destinationIndex > -1) {
      let newDestinationResourceIds = Array.from(
        edits.sectionGroups[excludedSectionGroupIndex].sections[destinationIndex].resourceIds,
      );
      newDestinationResourceIds = newDestinationResourceIds.concat(draggableIds);
      edits.sectionGroups[excludedSectionGroupIndex].sections[destinationIndex].resourceIds = newDestinationResourceIds;
    } else {
      edits.sectionGroups[excludedSectionGroupIndex].sections.push({
        ...sourceSection,
        uid: uuidv4(),
        clonedUid: sourceSection.uid,
        resourceIds: draggableIds,
      });
    }
  }

  return edits;
};
